import React from "react";
import { styled } from "@mui/material/styles";
import {
    FormControl, IconButton, InputLabel, OutlinedInput,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format, isValid, parse } from "date-fns";
import useStore from "../../store";
import PTMTooltip from "../PTMToolTip";

const generatePassword = () => {
    const length = 10;
    const lowerCase = ["a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z"];
    const upperCase = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z"];
    const numbers = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
    const symbols = ["#", "$", "%", "^", "&", "*", "(", ")"];
    const allChars = lowerCase.join("") + upperCase.join("") + numbers.join("") + symbols.join("");
    let pw = [];
    // eslint-disable-next-line no-loop-func
    while (!lowerCase.some((item) => pw.includes(item)) || !upperCase.some((item) => pw.includes(item)) || !numbers.some((item) => pw.includes(item)) || !symbols.some((item) => pw.includes(item))) {
        pw = Array.from(crypto.getRandomValues(new Uint32Array(length)))
            .map((x) => allChars[x % allChars.length]);
    }
    return pw.join("");
};

const StyledFormControl = styled(FormControl, {
    shouldForwardProp: (prop) => !["darkMode", "multiline", "label", "inputLink"].includes(prop),
})(({
    theme, darkMode, multiline, label, inputLink, inputStyle,
}) => ({
    "& .Mui-disabled fieldset": {
        backgroundColor: theme.palette.action.disabledBackground,
    },
    "& .Mui-disabled": {
        color: `${theme.palette.text.primary}!important`,
        WebkitTextFillColor: "initial",
    },
    "&>.MuiGrid-container": {
        marginTop: 10,
        marginBottom: -4,
        "& .MuiGrid-item": {
            paddingTop: 4,
        },
    },
    "& .MuiOutlinedInput-root": {
        background: "none",
        fontSize: 12,
        margin: 0,
        padding: 0,
        colorScheme: (darkMode ? "dark" : "light"),
        "& .MuiOutlinedInput-notchedOutline": {
            "&  legend": {
                border: "none",
            },
            top: 0,
            ...label ? {
                marginTop: 10,
                height: !multiline ? 24 : "auto",
            } : {
                height: !multiline ? 24 : "auto",
            },
        },
        "& .MuiOutlinedInput-input": {
            paddingTop: 4,
            paddingBottom: 2,
            paddingLeft: 3,
            paddingRight: 3,
        },
        "& .MuiIconButton-root": {
            minWidth: 16,
            marginRight: 4,
        },

        ...label ? {
            marginTop: 4,
            "&>*:not(.MuiOutlinedInput-notchedOutline):not(span)": {
                marginTop: 12,
            },
            // fix for PTMInput-switch endAdornment
            "& .PTMInput-switch": {
                // background: "orange",
                width: "20%",
                paddingTop: "4px",
                marginTop: -2,
                "& .MuiGrid-container": {
                    marginTop: 12,
                },
            },
            "& .MuiIconButton-root": {
                height: 21,
            },
        } : {
            "& .MuiSelect-iconOutlined": {
                marginTop: "1px!important",
            },
            "&>*:not(.MuiOutlinedInput-notchedOutline):not(.MuiSelect-select)": {
                marginTop: 2,
            },
            "& .MuiSelect-select": {
                marginTop: 0.75,
            },
            "& .MuiSelect-outlined": {

            },
        },

        ...inputLink && {
            "& .Mui-disabled": {
                cursor: "pointer",
                color: "#4594AD",
                textDecoration: "underline",
                "&:hover": {
                    color: "inherit",
                },
            },
        },

        "& .MuiInputBase-input": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...inputStyle,
        },
    },
    "& .MuiInputLabel-shrink": {
        fontSize: 12,
        transform: "translate(2px, -7px) scale(1) !important",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
        display: "none",
    },
    // fix for PTMInput-switch endAdornment
    "& .MuiInputBase-adornedEnd>.PTMInput-switch": {
        marginTop: "-5px!important",
    },
    ...label ? {
        height: !multiline ? 36 : "auto",
    } : {
        height: !multiline ? 24 : "auto",
    },
}));

const CustomTextField = React.memo((props) => {
    const {
        fullWidth,
        disabled,
        required,
        className,
        label,
        defaultValue,
        name,
        placeholder,
        type,
        value,
        onChange,
        onBlur,
        onKeyDown,
        onClick,
        startAdornment,
        endAdornment,
        sx,
        inputRef,
        error,
        // fieldState,
        multiline,
        step,
        rows,
        inputProps,
        allowShowPassword,
        allowGeneratePassword,
        customGeneratePasswordFunction,
        autoFocus,
        allowCopy,
        clearField,
        inputLink,
        inputStyle,
        dateParseTemplate,
        onContextMenu,
    } = props;
    const darkMode = useStore((store) => store.app.darkMode);

    const [showPassword, setShowPassword] = React.useState(false);
    const [toolTipText, setToolTipText] = React.useState("Inhalt kopieren");
    const getPassword = async () => {
        if (customGeneratePasswordFunction) {
            onChange({ target: { value: await customGeneratePasswordFunction() } });
        } else {
            onChange({ target: { value: generatePassword() } });
        }
        setShowPassword(true);
    };
    const copyToClipboard = (e, val) => {
        e.preventDefault();
        e.stopPropagation();

        setToolTipText("Kopiert");

        const clippableTitle = val;
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(clippableTitle);
            // If website is not secure run this code
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = clippableTitle;
            textArea.style.position = "fixed";
            textArea.style.left = "-999999px";
            textArea.style.top = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            return new Promise((res, rej) => {
                /* eslint-disable-next-line no-unused-expressions */
                document.execCommand("copy") ? res() : rej();
                textArea.remove();
            });
        }
        setTimeout(() => {
            setToolTipText("Inhalt kopieren");
        }, 1000);
    };

    const formatDate = (date) => {
        // solve problem with date input not rendering date when is on edit
        if (dateParseTemplate) {
            const parsedDate = parse(date, dateParseTemplate, new Date());
            if (isValid(parsedDate)) {
                return format(parsedDate, "yyyy-MM-dd");
            }
        }
        return date;
    };

    return (
        <StyledFormControl
            fullWidth={fullWidth}
            disabled={disabled}
            error={error}
            required={required}
            sx={sx}
            darkMode={darkMode}
            multiline={multiline}
            label={label}
            inputLink={inputLink}
            inputStyle={inputStyle}
            // className={!label ? `${classes.base} ${classes.noLabel}` : `${classes.base} ${classes.label} ${inputLink && classes.isLink}`}
        >
            {label && (
                <InputLabel>
                    {label}
                </InputLabel>
            )}
            <OutlinedInput
                className={className}
                onClick={onClick}
                fullWidth={fullWidth}
                label={label}
                name={name}
                placeholder={placeholder}
                {...allowShowPassword ? { type: showPassword ? "text" : "password" } : { type }}
                defaultValue={defaultValue}
                value={type === "date" ? formatDate(value) : value}
                onChange={onChange}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                autoFocus={autoFocus}
                onContextMenu={onContextMenu}
                startAdornment={(
                    <>
                        {startAdornment}
                        {allowShowPassword && (
                            <IconButton onClick={(() => setShowPassword(!showPassword))}>
                                <FontAwesomeIcon icon={showPassword ? "eye-slash" : "eye"} />
                            </IconButton>
                        )}
                        {allowGeneratePassword && (
                            <IconButton onClick={getPassword}>
                                <FontAwesomeIcon icon="key" />
                            </IconButton>
                        )}
                    </>
                )}
                endAdornment={(
                    <>
                        {allowCopy && (
                            <IconButton onClick={(e) => copyToClipboard(e, value)}>
                                <PTMTooltip title={toolTipText}>
                                    <FontAwesomeIcon icon="copy" />
                                </PTMTooltip>
                            </IconButton>
                        )}
                        {endAdornment}
                        {clearField && value && (
                            <IconButton onClick={() => onChange({ target: { value: "" } })}>
                                {/* <PTMTooltip title={toolTipText}> */}
                                <FontAwesomeIcon icon="times" />
                                {/* </PTMTooltip> */}
                            </IconButton>
                        )}
                    </>
                )}
                inputRef={inputRef}
                multiline={multiline}
                rows={rows}
                inputProps={{
                    ...inputProps,
                    max: inputProps?.max ? inputProps.max : "9999-12-31",
                    min: inputProps?.min && inputProps.min,
                    step,
                }}
            />

            {/* </div> */}
        </StyledFormControl>
    );
});

export default CustomTextField;
